import React, { useEffect, useState } from 'react'
import styles from './fileManager.jcss'
import { Header } from './Header/Header'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import translate from 'core/translate'
import Icon from 'blocks.simple/icon/icon'
import { colors } from 'theme'
import fileSize from 'filesize'
import moment from 'moment/moment'
import { api } from 'core/api/ConnectionManager'
import Button from 'blocks.simple/button/button'
import { number } from 'prop-types'
import EditText from '../../../../blocks.simple/editText/editText'

interface IFile {
    type: string
    data: {
        path: string
        name: string
        lastModified: number
        permission: {
            upload?: boolean
            rename?: boolean
            delete?: boolean
            run?: boolean
            silentInstall?: boolean
        }
        sizeBytes?: number
    }
}

export const FileManager = ({
    digitalSignageId,
    downloadStatus,
}: {
    digitalSignageId: number
    downloadStatus: { status: string; progress: number } | null
}) => {
    const [pathsForDelete, setPathsForDelete] = useState<string[]>([])
    const [newPathName, setNewPathName] = useState<string>('')
    const [pathForRename, setPathForRename] = useState<string>('')
    const [multipleContextmenuOpened, setMultipleContextmenuOpened] = useState<boolean>(false)
    const [selectedItem, setSelectedItem] = useState<string>('')
    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [path, setPath] = useState<string>('/')
    const [files, setFiles] = useState<IFile[]>([])
    const [newFolderName, setNewFolderName] = useState<string>('')
    const [createNewFolder, setCreateNewFolder] = useState<string>('')

    const onUpdateDirectory = () => {
        console.log('update directory')

        getFiles()
    }

    const onUpDirectory = () => {
        console.log('up directory')

        const upValue = path.substr(0, path.lastIndexOf('/'))

        if (upValue) {
            getFiles(upValue)
        }
    }

    const onRootDirectory = () => {
        api.send('fileManagerGetFiles', { digitalSignageId, path: '/' })
    }

    const getFiles = (updatePath?: string) => {
        console.log('get files')

        api.send('fileManagerGetFiles', { digitalSignageId, path: updatePath || path })
    }

    const clearSelectItem = (e: any) => {
        if (!e.target.classList.contains('treeDots_icon')) {
            setSelectedItem('')
            setMultipleContextmenuOpened(false)
        }
    }

    const onSelectItem = (path: string) => {
        if (selectedItems.includes(path)) {
            setSelectedItems(selectedItems.filter((item) => path !== item))

            return
        }

        setSelectedItems([...selectedItems, path])
    }

    const onSelectAll = () => {
        if (selectedItems.length !== files.length) {
            setSelectedItems(files.map((file) => file.data.path))

            return
        }

        setSelectedItems([])
    }

    const onDeleteAll = () => {
        setPathsForDelete(selectedItems)
    }

    const onDeleteFile = (path: string) => {
        console.log('delete file')

        setPathsForDelete([path])
        setSelectedItem('')
    }

    const onConfirmDeleteFile = () => {
        api.send('fileManagerDeleteFile', { digitalSignageId, paths: pathsForDelete }).then(() => {
            setPathsForDelete([])
        })
    }

    const onCancelDelete = () => {
        setPathsForDelete([])
    }

    const onRenameFile = (item: IFile) => {
        console.log('rename file')

        setPathForRename(item.data.path)
        setNewPathName(item.data.name)
        setSelectedItem('')
    }

    const onConfirmRenameFile = () => {
        api.send('fileManagerRenameFile', { digitalSignageId, path: pathForRename, name: newPathName })

        setTimeout(() => {
            onCancelRename()
        }, 2000)
    }

    const onCancelRename = () => {
        setNewPathName('')
        setPathForRename('')
    }

    const onDownloadFile = (path: string) => {
        console.log('download file')

        api.send('fileManagerDownloadFile', { digitalSignageId, paths: [path] })

        setSelectedItem('')
    }

    const onDownloadAll = () => {
        api.send('fileManagerDownloadFile', { digitalSignageId, paths: selectedItems })
        setSelectedItems([])
    }

    const onRunFile = (path: string) => {
        console.log('run file')

        api.send('fileManagerRunFile', { digitalSignageId, path })

        setSelectedItem('')
    }

    const onSilentInstall = (path: string) => {
        console.log('silent install')

        api.send('fileManagerSilentInstall', { digitalSignageId, path })

        setSelectedItem('')
    }

    const createFolder = () => {
        setCreateNewFolder(path)
    }

    const onCreateFolder = () => {
        if (!createNewFolder || !newFolderName) return

        console.log('create folder')

        api.send('fileManagerCreateFolder', { digitalSignageId, path: createNewFolder, name: newFolderName })

        setNewFolderName('')
        onCancelCreateFolder()
    }

    const onCancelCreateFolder = () => {
        setCreateNewFolder('')
    }

    const renderFilesAndFolders = (list: IFile[]) => {
        return list.map((item, index: number) => {
            const selected = selectedItem === item.data.path

            return (
                <tr style={{ backgroundColor: !(index % 2) ? '#272727' : '#323232' }}>
                    <td>
                        <Checkbox
                            mod="white"
                            checked={selectedItems.includes(item.data.path)}
                            onClick={() => {
                                onSelectItem(item.data.path)
                            }}
                        />
                    </td>
                    <td>
                        <div
                            className={styles.name}
                            onDoubleClick={() => {
                                if (item.type === 'folder') {
                                    getFiles(item.data.path)
                                }
                            }}
                        >
                            {item.type === 'file' ? (
                                <Icon
                                    type={'fileItem'}
                                    color={colors.white}
                                    containerClass={styles.instanceIcon}
                                    size={15}
                                />
                            ) : (
                                <Icon
                                    type={'folder'}
                                    color={colors.blueAccent}
                                    containerClass={styles.instanceIcon}
                                    size={15}
                                />
                            )}
                            {pathForRename === item.data.path ? (
                                <form
                                    className={styles.renameForm}
                                    onSubmit={(e: any) => {
                                        e.preventDefault()

                                        onConfirmRenameFile()
                                    }}
                                >
                                    <input
                                        value={newPathName}
                                        onInput={(e: any) => setNewPathName(e.target.value)}
                                        className={styles.renameInput}
                                        autoFocus
                                    />
                                </form>
                            ) : (
                                item.data.name
                            )}
                        </div>
                    </td>
                    <td>{moment(item.data.lastModified).format('DD.MM.YYYY HH:mm:ss')}</td>
                    <td>{item.data.sizeBytes ? fileSize(item.data.sizeBytes) : '--'}</td>
                    <td className={selected ? styles.selected : ''}>
                        {(item.data.permission.rename ||
                            item.data.permission.delete ||
                            item.data.permission.upload ||
                            item.data.permission.run) && (
                            <div
                                onClick={() => {
                                    setSelectedItem(item.data.path)
                                    setMultipleContextmenuOpened(false)
                                }}
                            >
                                <Icon
                                    type={'treeDots'}
                                    color={colors.grey}
                                    containerClass={styles.instanceIcon}
                                    size={16}
                                />
                            </div>
                        )}
                        {selected && (
                            <div
                                className={styles.contextMenu}
                                onClick={(e) => {
                                    e.nativeEvent.stopImmediatePropagation()
                                }}
                            >
                                {item.data.permission.rename && (
                                    <div className={styles.contextMenuItem} onClick={() => onRenameFile(item)}>
                                        {translate('rename')}
                                    </div>
                                )}
                                {item.data.permission.delete && (
                                    <div
                                        className={styles.contextMenuItem}
                                        onClick={() => onDeleteFile(item.data.path)}
                                    >
                                        {translate('delete')}
                                    </div>
                                )}
                                {item.data.permission.upload && (
                                    <div
                                        className={styles.contextMenuItem}
                                        onClick={() => onDownloadFile(item.data.path)}
                                    >
                                        {translate('download')}
                                    </div>
                                )}
                                {item.data.permission.run && (
                                    <div className={styles.contextMenuItem} onClick={() => onRunFile(item.data.path)}>
                                        {translate('run')}
                                    </div>
                                )}
                                {item.data.permission.silentInstall && (
                                    <div
                                        className={styles.contextMenuItem}
                                        onClick={() => onSilentInstall(item.data.path)}
                                    >
                                        {translate('silentInstall')}
                                    </div>
                                )}
                            </div>
                        )}
                    </td>
                </tr>
            )
        })
    }

    useEffect(() => {
        document.addEventListener('click', clearSelectItem)

        return () => document.removeEventListener('click', clearSelectItem)
    }, [])

    useEffect(() => {
        getFiles()
    }, [])

    useEffect(() => {
        // setFiles([
        //     {
        //         "type": "file",
        //         "data": {
        //             "path": "storage/0/Downloads/report.pdf",
        //             "name": "report.pdf",
        //             "lastModified": 1716940800000,
        //             "permission": {
        //                 "upload": false,
        //                 "rename": true,
        //                 "delete": true,
        //                 "run": true
        //             },
        //             "sizeBytes": 204800
        //         }
        //     },
        //     {
        //         "type": "file",
        //         "data": {
        //             "path": "storage/0/Downloads/report1.pdf",
        //             "name": "report.pdf",
        //             "lastModified": 1716940800000,
        //             "permission": {
        //                 "upload": true,
        //                 "rename": true,
        //                 "delete": true,
        //                 "run": true
        //             },
        //             "sizeBytes": 204800
        //         }
        //     },
        //     {
        //         "type": "folder",
        //         "data": {
        //             "path": "storage/0/Downloads/reports",
        //             "name": "reports",
        //             "lastModified": 1716940800000,
        //             "permission": {
        //                 "rename": false,
        //                 "delete": false,
        //                 "upload": true,
        //                 "run": false
        //             }
        //         }
        //     },
        // ])

        const listenersId: string[] = []
        api.addObserver(
            'fileManagerFilesReceived',
            (res: any) => {
                if (res && res.digitalSignageId === digitalSignageId) {
                    setPath(res.path)
                    setFiles(res.entries)
                    onCancelRename()
                }
            },
            listenersId
        )

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [])

    return (
        <div
            className={styles.fileManager}
            onClick={(e: any) => {
                if (
                    e.target.className === styles.renameInput.toString() ||
                    e.target.className === styles.contextMenuItem.toString()
                )
                    return

                onCancelRename()
            }}
        >
            <Header
                onCreateFolder={createFolder}
                onUpDirectory={onUpDirectory}
                onUpdateDirectory={onUpdateDirectory}
                onRootDirectory={onRootDirectory}
                setPath={setPath}
                path={path}
            />
            {downloadStatus && (
                <div className={styles.downloadStatus}>
                    {`${translate(downloadStatus.status)} ${downloadStatus.progress.toString()}%`}
                </div>
            )}
            <div className={styles.tableWrapper}>
                <table className={styles.filesTable}>
                    <tbody>
                        <tr>
                            <th>
                                <Checkbox
                                    mod="white"
                                    checked={selectedItems.length > 0 && selectedItems.length === files.length}
                                    style={{ marginRight: 0 }}
                                    onClick={onSelectAll}
                                />
                            </th>
                            <th>{translate('name')}</th>
                            <th>{translate('updatedAt')}</th>
                            <th>{translate('size')}</th>
                            <th>
                                {!!selectedItems.length && (
                                    <>
                                        <Icon
                                            type={'treeDots'}
                                            color={colors.white}
                                            containerClass={styles.instanceIcon}
                                            size={16}
                                            onClick={() => {
                                                setMultipleContextmenuOpened(!multipleContextmenuOpened)
                                                setSelectedItem('')
                                            }}
                                        />
                                        {multipleContextmenuOpened && (
                                            <div
                                                className={styles.contextMenu}
                                                onClick={(e) => {
                                                    e.nativeEvent.stopImmediatePropagation()
                                                }}
                                            >
                                                <div className={styles.contextMenuItem} onClick={onDeleteAll}>
                                                    {translate('delete')}
                                                </div>
                                                <div className={styles.contextMenuItem} onClick={onDownloadAll}>
                                                    {translate('download')}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </th>
                        </tr>
                        {renderFilesAndFolders(files)}
                    </tbody>
                </table>
            </div>
            {!!pathsForDelete.length && (
                <div className={styles.modalWrapper}>
                    <div className={styles.modalInner}>
                        <div className={styles.modalDescription}>{translate('deleteFilesConfirm')}</div>
                        <div className={styles.modalButtons}>
                            <Button mod={'withBorder'} className={styles.modalButton} onClick={onCancelDelete}>
                                {translate('cancel')}
                            </Button>
                            <Button mod="fill" className={styles.modalButton} onClick={onConfirmDeleteFile}>
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {createNewFolder && (
                <div className={styles.modalWrapper}>
                    <div className={styles.modalInner}>
                        <div className={styles.modalTitle}>{translate('createNewFolder')}</div>
                        <div className={styles.modalInputWrapper}>
                            <EditText
                                label={translate('folderName')}
                                value={newFolderName}
                                onChange={setNewFolderName}
                                placeholder={translate('newFolderName')}
                            />
                        </div>
                        <div className={styles.modalButtons}>
                            <Button mod={'withBorder'} className={styles.modalButton} onClick={onCancelCreateFolder}>
                                {translate('cancel')}
                            </Button>
                            <Button mod="fill" className={styles.modalButton} onClick={onCreateFolder}>
                                {translate('create')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
